import React, { useEffect, useState } from 'react';
import {
  Container, Paper, Typography, Box, Card, CardContent, Button,
  Chip, LinearProgress, Alert, Divider, Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  PointOfSale as PosIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  LocalBar as BarIcon,
  MoneyOff as MoneyOffIcon,
  Checklist as ChecklistIcon,
  AddShoppingCart as AddCartIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { todayBusinessDate , resolveBoundaryHour } from '../utils/businessDate';
import { getSalesPeriodForDate } from '../utils/salesPeriod';
import { evaluatePlan, PLAN_LABELS, TIER_DESCRIPTIONS } from '../utils/planGate';

const yen = (n: number) => `¥${Math.round(n).toLocaleString()}`;

interface Summary {
  businessDate: string;
  todaySales: number;
  todaySlips: number;
  todayGuests: number;
  lastDate: string;
  lastSales: number;
  lastSlips: number;
  periodLabel: string;
  periodSlips: number;
  monthSlips: number;
  peakMonthlySlips: number;
  workingNow: number;
  activeEmployees: number;
  openBottles: number;
  receivableBalance: number;
  receivableCount: number;
  pendingBacks: number;
}

const EMPTY: Summary = {
  businessDate: '', todaySales: 0, todaySlips: 0, todayGuests: 0,
  lastDate: '', lastSales: 0, lastSlips: 0,
  periodLabel: '', periodSlips: 0, monthSlips: 0, peakMonthlySlips: 0,
  workingNow: 0, activeEmployees: 0,
  openBottles: 0, receivableBalance: 0, receivableCount: 0, pendingBacks: 0,
};

const Dashboard: React.FC = () => {
  const { shop } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState<Summary>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!shop) return;
      setLoading(true);
      setError(null);
      try {
        const tz = shop.timezone ?? 'Asia/Tokyo';
        // 深夜をまたぐので、暦の日付ではなく営業日で切る。
        const boundary = resolveBoundaryHour(shop);
        const bd = todayBusinessDate(tz, boundary);
        const period = getSalesPeriodForDate(
          new Date(), shop.salesPeriod?.closingDay ?? 'monthEnd',
          { timezone: tz, boundaryHour: boundary }
        );

        const byShop = (col: string) =>
          query(collection(db, col), where('shopId', '==', shop.id));

        const [cks, emps, att, bottles, recv, incentives] = await Promise.all([
          getDocs(byShop('checkouts')),
          getDocs(byShop('employees')),
          getDocs(query(collection(db, 'attendances'),
            where('shopId', '==', shop.id), where('businessDate', '==', bd))),
          getDocs(byShop('bottleKeeps')),
          getDocs(byShop('receivables')),
          getDocs(byShop('staffIncentives')),
        ]);

        let todaySales = 0, todaySlips = 0, periodSlips = 0, monthSlips = 0;
        const monthPrefix = bd.slice(0, 7);
        // 開店前は今夜の伝票が0になる。そのとき何も出ないと
        // 「壊れている」のか「まだ始まっていない」のか分からない。
        // 直近の営業日の実績も持っておく。
        const perDay = new Map<string, { sales: number; slips: number }>();
        // 月初は当月だけ見ると必ず少なく出る。直近3ヶ月の最大も渡して、
        // 月が変わるたびに段が下がったり上がったりしないようにする。
        const perMonth = new Map<string, number>();
        cks.docs.forEach((d) => {
          const c: any = d.data();
          if (c.status === 'cancelled' || c.voided) return;
          const date: string = c.businessDate ?? '';
          if (date === bd) { todaySales += c.total ?? 0; todaySlips++; }
          if (date >= period.startDate && date <= period.endDate) periodSlips++;
          if (date.startsWith(monthPrefix)) monthSlips++;
          if (date) {
            const mk = date.slice(0, 7);
            perMonth.set(mk, (perMonth.get(mk) ?? 0) + 1);
          }
          if (date && date !== bd) {
            const cur = perDay.get(date) ?? { sales: 0, slips: 0 };
            cur.sales += c.total ?? 0; cur.slips++;
            perDay.set(date, cur);
          }
        });
        const lastDate = Array.from(perDay.keys()).sort().pop() ?? '';
        const recentMonths = Array.from(perMonth.keys()).sort().slice(-3);
        const peakMonthlySlips = recentMonths.reduce(
          (mx, k) => Math.max(mx, perMonth.get(k) ?? 0), 0);
        const last = lastDate ? perDay.get(lastDate)! : { sales: 0, slips: 0 };

        const activeEmployees = emps.docs.filter(
          (d) => (d.data() as any).status !== 'retired'
        ).length;
        const workingNow = att.docs.filter((d) => {
          const a: any = d.data();
          return a.clockIn && !a.clockOut;
        }).length;

        const openBottles = bottles.docs.filter(
          (d) => (d.data() as any).status === 'kept' || (d.data() as any).status === 'open'
        ).length;

        let receivableBalance = 0, receivableCount = 0;
        recv.docs.forEach((d) => {
          const r: any = d.data();
          if (r.status === 'paid') return;
          receivableBalance += r.balance ?? 0;
          receivableCount++;
        });

        // 当期ぶんだけ数える。全期間だと、締めた月のぶんまで
        // 「締める前に承認してください」に混ざる。
        const pendingBacks = incentives.docs.filter((d) => {
          const i: any = d.data();
          return i.backStatus === 'pending' && !i.isShopShare
            && i.salesPeriodKey === period.periodKey;
        }).length;

        // 客数は伝票の人数を持っていないので出さない。
        // 持っていない数字を推測で埋めると、合わない数字が1つ増えるだけになる。
        setS({
          businessDate: bd, todaySales, todaySlips, todayGuests: 0,
          lastDate, lastSales: last.sales, lastSlips: last.slips,
          periodLabel: period.label, periodSlips, monthSlips, peakMonthlySlips,
          workingNow, activeEmployees, openBottles,
          receivableBalance, receivableCount, pendingBacks,
        });
      } catch (e: any) {
        console.error('ダッシュボードの読み込みに失敗:', e);
        setError(e?.message ?? '読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shop]);

  if (!shop) return null;

  const gate = evaluatePlan(shop.planTier as any, {
    slipsThisMonth: s.monthSlips,
    peakMonthlySlips: s.peakMonthlySlips,
    employeeCount: s.activeEmployees,
    shopCount: 1,
  });
  const limit = gate.limits.maxSlipsPerMonth;
  // 課金の判定に使うのは「当月」と「直近3ヶ月の最大」の大きいほう。
  const billing = Math.max(s.monthSlips, s.peakMonthlySlips);
  const ratio = limit === null ? 0 : Math.min(100, (billing / limit) * 100);

  const tiles = [
    { icon: <TrendingUpIcon />, label: '今夜の売上', value: yen(s.todaySales),
      sub: s.todaySlips > 0
        ? `営業日 ${s.businessDate}（朝${resolveBoundaryHour(shop)}時区切り）`
        : (s.lastDate ? `まだ会計がありません。前回 ${s.lastDate} は ${yen(s.lastSales)}` : `営業日 ${s.businessDate}`),
      color: '#1976d2' },
    { icon: <ReceiptIcon />, label: '今夜の伝票', value: `${s.todaySlips} 件`,
      sub: s.todaySlips > 0
        ? `平均 ${yen(s.todaySales / s.todaySlips)}`
        : (s.lastDate ? `前回 ${s.lastDate} は ${s.lastSlips} 件` : 'まだ会計がありません'),
      color: '#0288d1' },
    { icon: <PeopleIcon />, label: '出勤中', value: `${s.workingNow} 人`,
      sub: s.workingNow > 0 ? `在籍 ${s.activeEmployees} 人` : `在籍 ${s.activeEmployees} 人・まだ誰も出勤していません`,
      color: '#2e7d32' },
    { icon: <PaymentsIcon />, label: '未承認のバック', value: `${s.pendingBacks} 件`,
      sub: s.pendingBacks > 0 ? `${s.periodLabel} ぶん。締める前に承認してください` : '未承認はありません',
      color: '#ed6c02' },
  ];

  const secondary = [
    { icon: <BarIcon />, label: 'キープ中のボトル', value: `${s.openBottles} 本`, to: '/bottle-keeps' },
    { icon: <MoneyOffIcon />, label: '未収金（ツケ）', value: `${yen(s.receivableBalance)} / ${s.receivableCount} 件`, to: '/receivables' },
    { icon: <ReceiptIcon />, label: `${s.periodLabel} の伝票`, value: `${s.periodSlips} 件`, to: '/analytics' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">{shop.shopName}</Typography>
        <Typography variant="body2" color="text.secondary">
          店舗コード {shop.shopCode} ／ 締日{' '}
          {shop.salesPeriod?.closingDay === 'monthEnd' ? '月末' : `${shop.salesPeriod?.closingDay}日`}
        </Typography>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* すぐ押すもの */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button variant="contained" size="large" startIcon={<AddCartIcon />}
            onClick={() => navigate('/proxy-order')}>代理注文</Button>
          <Button variant="contained" size="large" color="secondary" startIcon={<PosIcon />}
            onClick={() => navigate('/mobile-order/checkout')}>会計・レジ</Button>
          <Button variant="outlined" size="large" startIcon={<ChecklistIcon />}
            onClick={() => navigate('/setup-check')}>開店前チェック</Button>
        </Stack>
      </Paper>

      {/* 今夜の数字 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {tiles.map((t) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={t.label}>
            <Card sx={{ height: '100%', borderLeft: `4px solid ${t.color}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: t.color, mb: 1 }}>
                  {t.icon}
                  <Typography variant="body2" color="text.secondary">{t.label}</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">{t.value}</Typography>
                <Typography variant="caption" color="text.secondary">{t.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {/* 残っているもの */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>残っているもの</Typography>
            <Stack divider={<Divider />} spacing={0}>
              {secondary.map((x) => (
                <Box key={x.label} sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  py: 1.5, cursor: 'pointer',
                }} onClick={() => navigate(x.to)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {x.icon}
                    <Typography variant="body1">{x.label}</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="bold">{x.value}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* 料金の段 */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">今月の伝票</Typography>
              <Chip size="small" label={PLAN_LABELS[gate.tier]} color={gate.overLimit ? 'warning' : 'default'} />
            </Box>
            <Typography variant="h3" fontWeight="bold">
              {billing.toLocaleString()}
              <Typography component="span" variant="h6" color="text.secondary">
                {limit === null ? ' 件' : ` / ${limit.toLocaleString()} 件`}
              </Typography>
            </Typography>
            {limit !== null && (
              <LinearProgress variant="determinate" value={ratio}
                color={ratio > 90 ? 'warning' : 'primary'} sx={{ my: 1.5, height: 8, borderRadius: 4 }} />
            )}
            <Typography variant="body2" color="text.secondary">{gate.message}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {TIER_DESCRIPTIONS[gate.tier].price}・{TIER_DESCRIPTIONS[gate.tier].note}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              当月 {s.monthSlips.toLocaleString()} 件・直近3ヶ月の最大 {s.peakMonthlySlips.toLocaleString()} 件。
              大きいほうで判定します。
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              上限を超えても会計は止まりません。お客様を待たせないためです。
            </Typography>
            <Button size="small" sx={{ mt: 1 }} onClick={() => navigate('/settings/plan')}>
              料金の段を見る
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
